/**
 * The name a voucher is shown under.
 *
 * Kept free of Vue so it can be unit tested with plain node
 * (tests/unit/voucherFileName.test.js), and shared so the attraction editor,
 * the accommodation editor and the viewer modal cannot drift apart again --
 * each had its own copy of this, and all three were wrong the same way.
 *
 * Every uploader prefixes the file so two uploads cannot collide in storage,
 * and none of that prefix was meant for a person to read. There are three
 * shapes in production:
 *
 *   attraction_id_123_24_Oct_2026_18:16:13_GMT_booking.pdf   VoucherUpload
 *   import_4aa7a64309a5_24_Oct_2026_18:16:13_GMT_booking.pdf document import
 *   1792865638824_4aa7a64309a5_booking.pdf                   older imports
 *   1792865638824_booking.pdf                                oldest imports
 *
 * The first two end in the same upload stamp, so one pattern handles both --
 * which is why the import now writes that stamp as well. The last two are
 * files already sitting in storage, so they are stripped by their own shape.
 */

/**
 * The uploader's timestamp: two-or-one digit day, three-letter month, year,
 * clock, GMT. Anchoring on either end is unsafe -- the prefix contains
 * underscores and so can the manager's own filename ('my booking_final v2.pdf')
 * -- but this shape appears nowhere else by accident. Mirrors _UPLOAD_STAMP in
 * travelerblog/whatsapp/day_details.py; keep the two together.
 */
const UPLOAD_STAMP = /_\d{1,2}_[A-Za-z]{3}_\d{4}_\d{1,2}:\d{2}:\d{2}_GMT_/;

/**
 * What a document import wrote before it used the stamp: a millisecond clock,
 * optionally followed by a random token. The token is hex and at least six
 * characters, so a manager's own '2026_invoice.pdf' is not mistaken for one.
 */
const LEGACY_IMPORT_PREFIX = /^\d{10,}_(?:[0-9a-f]{6,}_)?/;

/** The traveller-facing name inside a storage URL. */
function voucherFilename(url, fallback = '') {
  if (!url || typeof url !== 'string') return fallback;
  try {
    // The path only: a URL with no path at all would otherwise hand back its
    // host as the filename.
    let path;
    try {
      path = decodeURIComponent(new URL(url).pathname);
    } catch (e) {
      path = decodeURIComponent(url.split('?')[0]);
    }
    const name = path.split('/').filter(Boolean).pop();
    if (!name) return fallback;

    const stamp = UPLOAD_STAMP.exec(name);
    if (stamp) {
      const original = name.slice(stamp.index + stamp[0].length).trim();
      if (original) return original;
    }

    const stripped = name.replace(LEGACY_IMPORT_PREFIX, '');
    return stripped || name;
  } catch (e) {
    return fallback;
  }
}

module.exports = { UPLOAD_STAMP, LEGACY_IMPORT_PREFIX, voucherFilename };
